import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plane, TrendingDown, Hotel, Check } from 'lucide-react';
import { formatCurrency, getContinentByDestination } from '../lib/helpers';
import { getDestinationImage } from '../lib/images';
import { Badge } from './ui/badge';

export const PackageCard = ({ promotion, index }) => {
  const continent = getContinentByDestination(promotion.destination);
  const imageUrl = promotion.accommodation_images?.length > 0 
    ? promotion.accommodation_images[0] 
    : getDestinationImage(promotion.destination);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.15 }}
      data-testid={`package-card-${index}`}
    >
      <Link to={`/promotion/${promotion.id}`}>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="relative h-52 overflow-hidden">
            <img 
              src={imageUrl}
              alt={promotion.destination}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            <div className="absolute top-3 left-3">
              <Badge className="bg-[#00A8B5] text-white font-semibold text-xs px-3 py-1">
                PACOTE COMPLETO
              </Badge>
            </div>
            <div className="absolute bottom-3 left-3 right-3">
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-xs">
                  {continent}
                </Badge>
                {promotion.accommodation_nights && (
                  <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-xs">
                    {promotion.accommodation_nights} noites
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="mb-3">
              <h3 className="text-lg font-bold text-slate-900 mb-1" data-testid="package-route-title">
                {promotion.origin} &rarr; {promotion.destination}
              </h3>
              <p className="text-sm text-slate-500">{promotion.airline} &middot; {promotion.cabin}</p>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2">
                <Plane className="w-4 h-4 text-[#00A8B5] flex-shrink-0" />
                <span className="text-xs text-slate-600">Passagem aérea inclusa</span>
              </div>
              <div className="flex items-center gap-2">
                <Hotel className="w-4 h-4 text-[#00A8B5] flex-shrink-0" />
                <span className="text-xs text-slate-600 truncate">
                  {promotion.accommodation_name || 'Hospedagem inclusa'}
                </span>
              </div>
              {promotion.included_services?.length > 2 && (
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00A8B5] flex-shrink-0" />
                  <span className="text-xs text-slate-600">
                    +{promotion.included_services.length - 2} serviços inclusos
                  </span>
                </div>
              )}
            </div>

            <div className="border-t border-dashed border-slate-200 pt-3">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">A partir de</p>
                  <p className="text-2xl font-bold text-[#00A8B5]" data-testid="package-price">
                    {formatCurrency(promotion.calculated_price)}
                  </p>
                  <p className="text-xs text-slate-400">por pessoa</p>
                </div>
                
                {promotion.savings_percentage > 0 && (
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-green-600">
                      <TrendingDown className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Economia</span>
                    </div>
                    <div className="text-xl font-bold text-green-600" data-testid="package-savings">
                      {promotion.savings_percentage}%
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button 
              className="w-full mt-4 bg-[#00A8B5] hover:bg-[#008C99] text-white font-semibold py-3 px-6 rounded-full transition shadow-md hover:shadow-lg text-sm"
              data-testid="view-package-button"
            >
              Ver Detalhes do Pacote
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
